import { IconButton, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';
import { m, useScroll } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MotionContainer, varFade } from 'src/components/animate';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';
import { HEADER } from 'src/layouts/config-layout';
import { useLocales } from 'src/locales';
import { RouterLink } from 'src/routes/components';
import { bgBlur, textGradient } from 'src/theme/css';
import bannerMobile from '../../assets/images/banner-mobile.png';
import bannerTablet from '../../assets/images/banner-tablet.png';
import banner from '../../assets/images/banner.png';

// ----------------------------------------------------------------------

const SOCIALS = [
  {
    key: 'telegram',
    label: 'Telegram - Group',
    icon: 'mingcute:telegram-fill',
    color: '#fff',
    link: 'https://t.me/walkupglobal',
  },
  {
    key: 'telegram',
    label: 'Telegram - Channel',
    icon: 'mingcute:telegram-fill',
    color: '#fff',
    link: ' https://t.me/walkupannouncement',
  },
  {
    key: 'twitter',
    label: 'Twitter',
    icon: 'mdi:twitter',
    color: '#1DA1F2',
    link: 'https://twitter.com/walkupglobal',
  },
  {
    key: 'kakao',
    label: 'Kakao',
    icon: 'mingcute:kakao-talk-fill',
    color: '#1DA1F2',
    link: 'https://open.kakao.com/o/gKzv2hZf',
  },
  {
    key: 'discord',
    label: 'Discord',
    icon: 'ic:baseline-discord',
    color: '#1DA1F2',
    link: 'https://discord.gg/walkup',
  },
];

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100vh',
  position: 'relative',

  [theme.breakpoints.up('md')]: {
    top: 0,
    left: 0,
    position: 'fixed',
  },
}));

const StyledWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    marginTop: HEADER.H_DESKTOP_OFFSET,
  },
}));

const StyledVideoBackground = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: -1,

  '& video': {
    objectFit: 'cover',
  },
}));

const StyledTextGradient = styled(m.h1)(({ theme }) => ({
  ...textGradient(
    `300deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 25%, ${theme.palette.primary.main} 50%, ${theme.palette.warning.main} 75%, ${theme.palette.primary.main} 100%`
  ),
  padding: 0,
  marginTop: 8,
  lineHeight: 1,
  marginBottom: 24,
  textAlign: 'center',
  backgroundSize: '400%',
  fontSize: `2rem`, // ${64 / 16}
  fontFamily: "'Barlow', sans-serif",
  [theme.breakpoints.up('md')]: {
    fontSize: `2rem`, // ${96 / 16}
  },
}));

const StyledEllipseTop = styled('div')(({ theme }) => ({
  top: -80,
  width: 480,
  right: -80,
  height: 480,
  borderRadius: '50%',
  position: 'absolute',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

const StyledEllipseBottom = styled('div')(({ theme }) => ({
  height: 400,
  bottom: -200,
  left: '10%',
  right: '10%',
  borderRadius: '50%',
  position: 'absolute',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

const StyledPolygon = styled('div')(({ opacity = 1, anchor = 'left', theme }) => ({
  ...bgBlur({
    opacity,
    color: theme.palette.background.default,
  }),
  zIndex: 9,
  bottom: 0,
  height: 80,
  width: '50%',
  position: 'absolute',
  clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)',
  ...(anchor === 'left' && {
    left: 0,
    ...(theme.direction === 'rtl' && {
      transform: 'scale(-1, 1)',
    }),
  }),
  ...(anchor === 'right' && {
    right: 0,
    transform: 'scaleX(-1)',
    ...(theme.direction === 'rtl' && {
      transform: 'scaleX(1)',
    }),
  }),
}));

const StyledSocialIconBtn = styled(IconButton)(({ theme }) => ({
  padding: 10,
}));

// ----------------------------------------------------------------------

export default function HomeHero() {
  const { t } = useLocales();

  const lgUp = useResponsive('up', 'lg');
  const mdUp = useResponsive('up', 'md');
  const mdDown = useResponsive('down', 'md');
  const smDown = useResponsive('down', 'sm');

  const heroRef = useRef(null);

  const { scrollY } = useScroll();

  const [percent, setPercent] = useState(0);

  const getScroll = useCallback(() => {
    let heroHeight = 0;

    if (heroRef.current) {
      heroHeight = heroRef.current.offsetHeight;
    }

    scrollY.on('change', (scrollHeight) => {
      const scrollPercent = (scrollHeight * 100) / heroHeight;

      setPercent(Math.floor(scrollPercent));
    });
  }, [scrollY]);

  useEffect(() => {
    getScroll();
  }, [getScroll]);

  const opacity = 1 - percent / 100;

  const hide = percent > 120;

  const renderDescription = (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        height: 1,
        mx: 'auto',
        opacity: opacity > 0 ? opacity : 0,
        mt: {
          md: `-${HEADER.H_DESKTOP + percent * 2.5}px`,
        },
      }}
    >
      <Box flex={1} />

      <m.div variants={varFade().in}>
        <Stack spacing={1.5} direction={{ xs: 'column', sm: 'row' }} sx={{ mb: 10 }}>
          <Button
            component={RouterLink}
            // href={paths.dashboard.root}
            color="inherit"
            size="large"
            variant="contained"
            startIcon={<Iconify icon="ion:logo-google-playstore" width={36} />}
          >
            <Stack>
              <Typography variant="caption" lineHeight={1}>
                {t('walkup_lang_005')}
              </Typography>
              <Typography variant="h6" lineHeight={1}>
                Google Play
              </Typography>
            </Stack>
          </Button>

          <Button
            component={RouterLink}
            // href={paths.dashboard.root}
            color="inherit"
            size="large"
            variant="contained"
            startIcon={<Iconify icon="ion:logo-apple-appstore" width={36} />}
          >
            <Stack>
              <Typography variant="caption" lineHeight={1}>
                {t('walkup_lang_005')}
              </Typography>
              <Typography variant="h6" lineHeight={1}>
                App Store
              </Typography>
            </Stack>
          </Button>
        </Stack>
      </m.div>
    </Stack>
  );

  const renderPolygons = (
    <>
      <StyledPolygon />
      <StyledPolygon anchor="right" opacity={0.48} />
      <StyledPolygon anchor="right" opacity={0.48} sx={{ height: 48, zIndex: 10 }} />
      <StyledPolygon anchor="right" sx={{ zIndex: 11, height: 24 }} />
    </>
  );

  const renderEllipses = (
    <>
      {mdUp && <StyledEllipseTop />}
      <StyledEllipseBottom />
    </>
  );

  const renderSocialSidebar = (
    <Stack
      sx={{
        position: 'absolute',
        opacity: opacity > 0 ? opacity : 0,
        zIndex: 1,

        background: alpha('#fff', 0.2),
        backdropFilter: 'blur(10px)',
        top: '30%',
        right: 0,
        transform: 'translateY(-30%)',

        borderRadius: '8px 0 0 8px',
      }}
      p={1}
    >
      {SOCIALS.map((social) => (
        <Box component={m.div} variants={varFade().in}>
          <Tooltip arrow title={social.label}>
            <StyledSocialIconBtn
              size="small"
              onClick={() => {
                window.open(social.link);
              }}
            >
              <Iconify icon={social.icon} width={mdUp ? 20 : 15} color="#fff" />
            </StyledSocialIconBtn>
          </Tooltip>
        </Box>
      ))}
    </Stack>
  );

  const getBanner = () => {
    if (lgUp) return banner;
    if (mdUp) return bannerTablet;
    if (mdDown) return bannerMobile;
    return banner;
  };

  return (
    <>
      <StyledRoot
        ref={heroRef}
        sx={{
          backgroundImage: `url(${getBanner()})`,
          backgroundPosition: smDown ? 'top' : 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: smDown ? 'contain' : 'cover',
          backgroundColor: '#173a57',

          ...(hide && {
            opacity: 0,
          }),
        }}
      >
        <StyledWrapper>
          <Container component={MotionContainer} sx={{ height: 1 }}>
            {renderDescription}

            {renderSocialSidebar}

            {/* <StyledVideoBackground>
              <ReactPlayer
                url="https://assets-global.website-files.com/624181072db315237608dddf/64651cb1f74788a95b05d540_new-transcode.mp4"
                playing
                loop
                muted
                width="100%"
                height="100%"
              />

              <img src="./assets/images/home/banner.png" alt="" style={{ width: '100%', height: '100%' }} />
            </StyledVideoBackground> */}
          </Container>

          {renderEllipses}
        </StyledWrapper>
      </StyledRoot>

      {/* {mdUp && renderPolygons} */}

      <Box sx={{ height: { md: '100vh' } }} />
    </>
  );
}
